import React from 'react';

// Production error handling
class ErrorHandler {
  constructor() {
    this.isProduction = import.meta.env.PROD;
    this.errorCount = 0;
    this.maxErrors = 10;
  }

  logError(error, context = {}) {
    this.errorCount++;
    
    // Prevent error spam in production
    if (this.errorCount > this.maxErrors) {
      return;
    }

    const errorInfo = {
      message: error.message,
      stack: error.stack,
      context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      errorCount: this.errorCount
    };

    if (this.isProduction) {
      // In production, send to error reporting service
      this.sendToErrorService(errorInfo);
    } else {
      // In development, log to console
      console.error('Error:', errorInfo);
    }
  }

  sendToErrorService(errorInfo) {
    // Send to your error reporting service (e.g., Sentry, LogRocket, etc.)
    try {
      // Example: Send to your API endpoint
      fetch('/api/errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorInfo)
      }).catch(() => {
        // Silently fail if error reporting fails
      });
    } catch (e) {
      // Silently fail
    }
  }

  handleAsyncError(error, context = {}) {
    this.logError(error, context);
    
    // Return a user-friendly error message
    return {
      success: false,
      message: this.isProduction 
        ? 'An unexpected error occurred. Please try again.' 
        : error.message,
      error: this.isProduction ? null : error
    };
  }

  handlePromiseRejection(event) {
    event.preventDefault();
    this.logError(event.reason, { type: 'unhandledRejection' });
  }

  handleGlobalError(event) {
    this.logError(event.error, { type: 'globalError' });
  }

  init() {
    // Set up global error handlers
    window.addEventListener('unhandledrejection', this.handlePromiseRejection.bind(this));
    window.addEventListener('error', this.handleGlobalError.bind(this));
  }

  // Additional methods for compatibility with tests
  clearErrorLog() {
    this.errorCount = 0;
  }

  getErrorStats() {
    return {
      total: this.errorCount
    };
  }

  handleApiError(error, context, options = {}) {
    this.logError(error, { ...context, type: 'api', ...options });
    return this.handleAsyncError(error, context);
  }

  handleComponentError(error, errorInfo) {
    this.logError(error, {
      type: 'component',
      componentStack: errorInfo.componentStack
    });
  }

  handleValidationError(errors, field) {
    const error = new Error(`Validation failed for ${field}: ${errors.join(', ')}`);
    this.logError(error, { type: 'validation', field, errors });
    return this.handleAsyncError(error, { field, errors });
  }

  getUserFriendlyMessage(error, type = 'general') {
    if (error.message.includes('timeout')) {
      return 'The request timed out. Please try again.';
    }
    if (error.message.includes('network')) {
      return 'Network error. Please check your connection.';
    }
    if (error.message.includes('unauthorized') || error.message.includes('401')) {
      return 'You are not authorized to perform this action.';
    }
    return 'An unexpected error occurred. Please try again.';
  }
}

export const errorHandler = new ErrorHandler();

// Initialize error handling
errorHandler.init();

// React Error Boundary
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    errorHandler.logError(error, {
      type: 'reactErrorBoundary',
      componentStack: errorInfo.componentStack
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-white mb-4">Something went wrong</h1>
            <p className="text-gray-400 mb-6">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
